import React from 'react';
import { Box, Button } from '@mui/material';
import Icon from '@mdi/react';
import i18next from 'i18next';
import type { ISignaturePropertiesDialogProps } from '../../../types';

import styles from './SignaturePropertiesDialog.module.scss';
import { ApryseSDK } from '../../../pdf/ApryseSDK';
import { mdiCheckCircle, mdiCloseCircle } from '@mdi/js';
import { dateTimeFormat } from '../../../utils';

/**
 * Component that shows document's properties.
 */
const SignaturePropertiesDialog: React.FC<ISignaturePropertiesDialogProps> = ({
    onClose,
    signatureId,
}) => {
    const signatureData = ApryseSDK.signatureData[signatureId];

    const identitiesAndTrust = (): string => {
        if (!signatureData.hasTrustVerificationResult || !signatureData.trustVerificationResult) {
            return i18next.t('signaturePropsDialog.ENUMS.trustVerification.N');
        }

        return i18next.t(
            `signaturePropsDialog.ENUMS.trustVerification.${signatureData.timeOfTrustVerificationENUM}`,
            {
                timeOfTrustVerification: signatureData.timeOfTrustVerification?.toLocaleString(
                    'de-DE',
                    dateTimeFormat,
                ),
            },
        );
    };

    if (!signatureData) {
        return <></>;
    }

    return (
        <Box className={styles.outerShell}>
            <Box className={styles.tabsContainer}>
                <Box className={styles.validity}>
                    {signatureData.verificationStatus ? (
                        <Icon path={mdiCheckCircle} color={'green'} />
                    ) : (
                        <Icon path={mdiCloseCircle} color={'red'} />
                    )}
                    {i18next.t('signaturePropsDialog.text.validity', {
                        validity: i18next.t(
                            `signaturePropsDialog.text.validity-${
                                signatureData.verificationStatus ? 1 : 0
                            }`,
                            {
                                signer: signatureData.signer,
                            },
                        ),
                    })}
                </Box>
                <Box className={styles.content}>
                    <span className={styles.sectionTitle}>
                        {i18next.t('signaturePropsDialog.title.documentIntegrity')}
                    </span>
                    <Box>
                        <p>
                            {i18next.t(
                                `signaturePropsDialog.ENUMS.permissionsStatus.${signatureData.permissionsStatus}`,
                            )}
                        </p>
                        <p>
                            {i18next.t(
                                `signaturePropsDialog.ENUMS.digsigDocPermissions.${signatureData.digsigDocPermissions}`,
                            )}
                        </p>
                    </Box>

                    <span className={styles.sectionTitle}>
                        {i18next.t('signaturePropsDialog.title.identitesAndTrust')}
                    </span>
                    <Box>
                        <p>
                            {i18next.t(
                                `signaturePropsDialog.ENUMS.trustVerificationSuccess.${
                                    signatureData.wasTrustVerificationResultSuccessful ? 1 : 0
                                }`,
                            )}
                        </p>
                        <p>{identitiesAndTrust()}</p>
                    </Box>

                    <span className={styles.sectionTitle}>
                        {i18next.t('signaturePropsDialog.title.generalErrors')}
                    </span>
                    <Box>
                        {i18next.t(
                            `signaturePropsDialog.ENUMS.documentStatus.${signatureData.documentStatus}`,
                        )}
                    </Box>

                    <span className={styles.sectionTitle}>
                        {i18next.t('signaturePropsDialog.title.digestStatus')}
                    </span>
                    <Box>
                        <p>
                            {i18next.t(
                                `signaturePropsDialog.ENUMS.digestStatus.${signatureData.digestStatus}`,
                            )}
                        </p>
                        <p>
                            {`${i18next.t(
                                'signaturePropsDialog.text.usedDigestAlgorithm',
                            )}: ${i18next.t(
                                `signaturePropsDialog.ENUMS.digestAlgorithm.${signatureData.digestAlgorithm}`,
                            )}`}
                        </p>
                    </Box>
                </Box>
            </Box>
            <Button variant="contained" style={{ float: 'right' }} onClick={onClose}>
                {i18next.t('generic.button.close')}
            </Button>
        </Box>
    );
};

export default SignaturePropertiesDialog;
